import React, {
  FormEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckboxWrapper from "./checkbox-wrapper";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import useGetIncidentTypes from "../../../../api/incident-types/useGetIncidentTypes";
import useGetFacility from "../../../../api/facilities/useGetFacility";
import api from "../../../../api/axiosConfig";
import { BlueButton } from "../../../../components/ui/Buttons";
import IncidentTypesDataTypes from "../../../../types/incident-types-data-types";

function AddSiteBasedAlert({
  setSiteBasedAlertModal,
  setSuccessfullyAddedSBA,
}: {
  setSiteBasedAlertModal: React.Dispatch<SetStateAction<boolean>>;
  setSuccessfullyAddedSBA: React.Dispatch<SetStateAction<boolean>>;
}) {
  const navigate = useNavigate();
  const { client_id, facility_id } = useParams();
  const incidentTypes = useGetIncidentTypes();
  const formRef = useRef<HTMLFormElement>(null);
  const alertRadiusRef = useRef<HTMLInputElement>(null);
  const [successMessage, setSuccessMessage] = useState<boolean>(false);
  const facility = useGetFacility(facility_id!);
  console.log(facility);
  const nameRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    const formData = new FormData(formRef.current!);
    const mainSubTypesID = formData.getAll("sub-type");
    const severity = formData.getAll("severity");
    const types = mainSubTypesID;

    const data: {
      name: string;
      facilityID: string;
      clientID: string;
      radiusM: number;
      incidentType: FormDataEntryValue[];
      position: {
        type: string;
        coordinates: [number, number];
      };
      severity: {
        unknown: boolean;
        insignificant: boolean;
        limited: boolean;
        moderate: boolean;
        major: boolean;
        extreme: boolean;
      };
    } = {
      name: nameRef.current!.value,
      facilityID: facility_id!,
      clientID: client_id!,
      radiusM: parseFloat(alertRadiusRef.current!.value),
      incidentType: types,
      position: {
        type: "Point",
        coordinates: [
          facility.data!.data.position.coordinates[0],
          facility.data!.data.position.coordinates[1],
        ],
      },
      severity: {
        unknown: severity.includes("unknown"),
        insignificant: severity.includes("insignificant"),
        limited: severity.includes("limited"),
        moderate: severity.includes("moderate"),
        major: severity.includes("major"),
        extreme: severity.includes("extreme"),
      },
    };

    if (!data.facilityID) {
      alert("Facility ID not found");
    }
    if (!data.clientID) {
      alert("Client ID not found.");
    }

    api
      .post(`sba/${facility_id}`, data)
      .then((res) => {
        setSiteBasedAlertModal(false);
        setSuccessfullyAddedSBA(true);
        setTimeout(() => {
          setSuccessfullyAddedSBA(false);
          navigate("/sites");
        }, 2500);
      })
      .catch((err) => console.error(err));
  };

  const handleAddMoreSiteBasedAlerts = () => {
    const formData = new FormData(formRef.current!);
    const mainSubTypesID = formData.getAll("sub-type");
    const types = mainSubTypesID;
    const data: {
      name: string;
      facilityID: string;
      clientID: string;
      radiusM: number;
      incidentType: FormDataEntryValue[];
      position: {
        type: string;
        coordinates: [number, number];
      };
    } = {
      name: nameRef.current!.value,
      facilityID: facility_id!,
      clientID: client_id!,
      radiusM: parseFloat(alertRadiusRef.current!.value),
      incidentType: types,
      position: {
        type: "Point",
        coordinates: [
          facility.data!.data.position.coordinates[0],
          facility.data!.data.position.coordinates[1],
        ],
      },
    };

    api
      .post(`sba/${facility_id}`, data)
      .then((res) => {
        setSuccessfullyAddedSBA(true);
        setSiteBasedAlertModal(false);
        formRef.current!.reset();
        setTimeout(() => {
          setSuccessfullyAddedSBA(false);
          setSiteBasedAlertModal(true);
        }, 2500);
      })
      .catch((err) => console.error(err));
  };

  return incidentTypes.status !== "success" ? null : (
    <form
      className="p-8 bg-white rounded-md shadow-md flex gap-8 flex-col h-[60vh] overflow-y-auto"
      action={"sites"}
      ref={formRef}
    >
      <header className="h-10 w-full border-b flex items-center justify-center">
        <h4 className="font-semibold">Add Site-based Alert</h4>
        <button
          className="absolute top-5 right-5"
          type="button"
          onClick={() => setSiteBasedAlertModal(false)}
        >
          <CloseIcon />
        </button>
      </header>

      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="text-red font-semibold">*</span>
          <input
            name={"name"}
            type="text"
            placeholder="Site Name"
            className="h-10 w-full rounded-md shadow-md px-4"
            required
            ref={nameRef}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="text-red font-semibold">*</span>
          <input
            name={"alertRadius"}
            type="text"
            placeholder="Alert Radius (m)"
            className="h-10 w-full rounded-md shadow-md px-4"
            required
            ref={alertRadiusRef}
          />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex items-center gap-2">
          <span className="text-red font-semibold">*</span>
          <div className="h-10 w-full px-4 flex items-center opacity-50">
            <p>Incident Types:</p>
          </div>
        </div>
        {incidentTypes.data?.data.map((type: IncidentTypesDataTypes) => (
          <CheckboxWrapper incidentType={type} key={type.id} />
        ))}
      </div>

      <div className="">
        <select
          name="severity"
          id=""
          className="h-34 w-full rounded-md shadow-md"
          defaultValue={"select-severity"}
          multiple={true}
          required
        >
          <option value="select-severity" disabled>
            Select Severity
          </option>
          <option value="unknown">Unknown</option>
          <option value="insignificant">Insignificant</option>
          <option value="limited">Limited</option>
          <option value="moderate">Moderate</option>
          <option value="major">Major</option>
          <option value="extreme">Extreme</option>
        </select>
      </div>

      <div className="flex mx-auto flex-col gap-4 my-6 w-1/3">
        <BlueButton
          text="No site-based alert"
          onClick={() => setSiteBasedAlertModal(false)}
        />
        <BlueButton text="Save" onClick={handleSave} type="submit" />
        {/* <BlueButton
          text="Add more site-based alerts"
          onClick={handleAddMoreSiteBasedAlerts}
        /> */}
        {successMessage ? (
          <span className="text-center text-green">
            Added Site Based Alerts!
          </span>
        ) : null}
      </div>
    </form>
  );
}

export default AddSiteBasedAlert;
